/**
 * TaskActivityFeed.tsx — a task's change log, newest first, from
 * GET /tasks/{id}/activity (the tenant-scoped events log; the route 404s
 * unless the caller can see the task). Loading, empty and error are distinct:
 * a failed request is never shown as "No activity yet".
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Sparkles, Pencil, Trash2, ArrowRightLeft, ClipboardList, ScrollText, RotateCcw } from 'lucide-react';
import { apiClient } from '../packages/api-client';

/** Mirrors backend internal/events.Activity. */
interface ActivityEntry {
    id: string;
    event_type: string;
    actor_id?: string;
    actor_first_name?: string;
    actor_last_name?: string;
    actor_email?: string;
    changes: Record<string, unknown>;
    created_at: string;
}

/** Mirrors backend internal/pagination.Meta. */
interface ActivityPage {
    data: ActivityEntry[];
    meta: { page: number; limit: number; total: number; total_pages: number };
}

const EVENT_ICONS: Record<string, LucideIcon> = {
    task_created: Sparkles, task_updated: Pencil, task_deleted: Trash2,
    task_moved: ArrowRightLeft, task_restored: RotateCcw, default: ClipboardList,
};

const words = (v: unknown) => String(v ?? '').replace(/_/g, ' ');

function eventLabel(e: ActivityEntry): string {
    switch (e.event_type) {
        case 'task_created': return 'created this task';
        // The payload is the task's state after the update, not a diff.
        case 'task_updated': return 'updated this task';
        case 'task_moved': return e.changes.new_status ? `moved to ${words(e.changes.new_status)}` : 'moved this task';
        case 'task_deleted': return 'deleted this task';
        case 'task_restored': return 'restored this task';
        default: return words(e.event_type);
    }
}

function actorName(e: ActivityEntry) {
    const full = `${e.actor_first_name ?? ''} ${e.actor_last_name ?? ''}`.trim();
    return full || e.actor_email || 'Someone';
}

const muted: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--space-4)' };

export function TaskActivityFeed({ taskId }: { taskId: string }) {
    const [entries, setEntries] = useState<ActivityEntry[]>([]);
    const [meta, setMeta] = useState<ActivityPage['meta'] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    // A response for a task we have since navigated away from is dropped.
    const current = useRef(taskId);
    current.current = taskId;

    const load = useCallback((page: number) => {
        setLoading(true);
        setError(false);
        return apiClient.get<ActivityPage>(`/tasks/${taskId}/activity`, { params: { page } })
            .then(r => {
                if (current.current !== taskId) return;
                setEntries(prev => (page === 1 ? r.data.data : [...prev, ...r.data.data]));
                setMeta(r.data.meta);
            })
            .catch(() => { if (current.current === taskId) setError(true); })
            .finally(() => { if (current.current === taskId) setLoading(false); });
    }, [taskId]);

    useEffect(() => {
        setEntries([]);
        setMeta(null);
        void load(1);
    }, [load]);

    if (error && entries.length === 0) return (
        <div role="alert" style={muted}>
            Couldn&apos;t load activity.{' '}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void load(1)}>Retry</button>
        </div>
    );
    if (loading && entries.length === 0) return <div className="spinner" style={{ margin: '1rem auto' }} />;
    if (entries.length === 0) return <p style={muted}>No activity yet.</p>;

    const hasMore = meta !== null && meta.page < meta.total_pages;

    return (
        <div>
            <div style={{ fontWeight: 600, marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}><ScrollText size={16} aria-hidden="true" /> Activity ({meta?.total ?? entries.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {entries.map(e => (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', fontSize: 'var(--font-size-sm)' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {(() => { const Icon = EVENT_ICONS[e.event_type] ?? EVENT_ICONS.default; return <Icon size={14} aria-hidden="true" />; })()}
                        </div>
                        <div style={{ flex: 1 }}>
                            <strong>{actorName(e)}</strong>{' '}
                            <span style={{ color: 'var(--color-text-muted)' }}>{eventLabel(e)}</span>
                        </div>
                        <div style={{ flexShrink: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                            {new Date(e.created_at).toLocaleDateString()} {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                ))}
            </div>
            {error && <p role="alert" style={muted}>Couldn&apos;t load more activity.</p>}
            {hasMore && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={loading}
                    onClick={() => void load(meta.page + 1)} style={{ marginTop: 'var(--space-2)' }}>
                    {loading ? 'Loading…' : 'Load more'}
                </button>
            )}
        </div>
    );
}
